"use client";

import { useRouter } from "next/navigation";
import { startTransition, useState } from "react";
import { Form } from "react-aria-components";
import { useMutation } from "urql";

import { graphql } from "@whitecircle/graphql";

import { useAuth } from "@/components/auth";
import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import { Copy } from "@/components/icons/copy";
import { Success } from "@/components/icons/success";
import { Input } from "@/components/input";
import { useReset } from "@/components/urql";
import { useDomain } from "@/hooks/use-domain";

const mutation = graphql(`
  mutation CreateTeam($name: String!, $slug: String!, $domains: String) {
    team {
      create(name: $name, slug: $slug, domains: $domains) {
        id
        slug
      }
    }
  }
`);

interface CreateTeamForm {
	name: HTMLInputElement;
	slug: HTMLInputElement;
	domains: HTMLInputElement;
}

export function CreateTeam() {
	const [{ fetching }, createTeam] = useMutation(mutation);
	const [isCopied, setIsCopied] = useState(false);
	const [slug, setSlug] = useState("");
	const router = useRouter();
	const reset = useReset();
	const domain = useDomain();
	const auth = useAuth();

	const onCopy = () => {
		if (slug) {
			const fullUrl = `https://${domain}/${slug}`;

			navigator.clipboard.writeText(fullUrl).then(() => {
				setIsCopied(true);
				setTimeout(() => setIsCopied(false), 1000); // Reset after 2 seconds
			});
		}
	};

	return (
		<Dialog title="New workspace">
			<Form
				onSubmit={(e) => {
					e.preventDefault();
					const form = e.currentTarget as unknown as CreateTeamForm;
					const name = form.name.value;
					const slug = form.slug.value;
					const domains = form.domains.value;

					createTeam({ name, slug, domains }).then(async ({ data }) => {
						if (data) {
							await auth.teams.set({ teamId: data.team.create.id });
							startTransition(async () => {
								router.refresh();
								reset();
							});
						}
					});
				}}
				className="flex flex-col gap-8"
			>
				<Input name="name" label="Name" placeholder="Your team name" />
				<Input
					name="slug"
					className={"relative"}
					onChange={(e) => {
						setSlug(e);
					}}
					label="Shareable link for your team"
					placeholder="your-team-name"
				>
					<div className="inset-shadow-glow rounded-full border px-3 py-1 relative left-2 -mr-1">
						{domain}/
					</div>

					<div
						className={`absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer transition-all ${slug ? "opacity-100" : "opacity-0"}`}
					>
						{isCopied ? (
							<Success className="text-white" width={28} height={28} />
						) : (
							<Copy
								width={24}
								height={24}
								className="text-gray-600 hover:text-gray-200 transition-all"
								onClick={onCopy}
							/>
						)}
					</div>
				</Input>
				<Input
					name="domains"
					label="Allowed domains for log-in"
					placeholder="example.com"
				/>
				<Button type="submit" isPending={fetching} className="ml-auto">
					Create workspace
				</Button>
			</Form>
		</Dialog>
	);
}
